import { useEffect, useId, useRef, useState } from 'react';
import { BlockProps } from '@/components/Block/Block';
import P from '@/components/Redactie/P/P';
import BlockEditorContainer from '@/components/Block/BlockEditorContainer';
import { UserMessageBlockViewerContainer } from '@/components/Block/BlockViewerContainer';
import { mdiCheckCircle, mdiPen } from '@mdi/js';
import { useBlockLocalUserData } from '@/hooks/useBlockLocalUserData';
import TextButton from '@/components/Button/TextButton';
import EmojiText from '@/components/EmojiText/EmojiText';
import useLocalStorage from '@/hooks/useLocalStorage';
import styles from './OpenQuestionBlock.module.sass';

interface OpenQuestionBlockData {
    question: string;
}
interface OpenQuestionBlockLocalData {
    answer: string;
}

const OpenQuestionBlock = ({
    subjectId,
    id,
    data,
    uuid,
    updatedAt,
    isEditable,
    isEditorPreview,
    isAnimated,
    onFinished,
    onEdited,
}: BlockProps) => {
    const [blockData, setBlockData] = useState<OpenQuestionBlockData>({
        question: data.question ?? 'Wat heb je geleerd?',
    });

    if (isEditable)
        return (
            <OpenQuestionBlockEditor
                uuid={uuid}
                blockData={blockData}
                setBlockData={setBlockData}
                onEdited={onEdited}
            />
        );
    else
        return (
            <OpenQuestionBlockViewer
                subjectId={subjectId}
                id={id}
                blockData={blockData}
                updatedAt={updatedAt}
                isEditorPreview={isEditorPreview}
                isAnimated={isAnimated}
                onFinished={onFinished}
            />
        );
};

const OpenQuestionBlockEditor = ({
    uuid,
    blockData,
    setBlockData,
    onEdited,
}: {
    uuid: BlockProps['uuid'];
    blockData: OpenQuestionBlockData;
    setBlockData: (data: OpenQuestionBlockData) => void;
    onEdited: BlockProps['onEdited'];
}) => {
    useEffect(() => {
        onEdited?.({
            uuid,
            data: JSON.parse(JSON.stringify(blockData)),
            attachments: {},
        });
    }, [blockData]);

    return (
        <BlockEditorContainer className="bg-red-200">
            <P
                isEditable
                value={blockData.question}
                onChange={(value) => {
                    setBlockData({ ...blockData, question: value });
                }}
                placeholder="Vraag"
                rows={1}
            />
        </BlockEditorContainer>
    );
};

const MIN_ANSWER_LENGTH = 1;

const OpenQuestionBlockViewer = ({
    subjectId,
    id,
    blockData,
    updatedAt,
    isEditorPreview,
    isAnimated,
    onFinished,
}: {
    subjectId: BlockProps['subjectId'];
    id: BlockProps['id'];
    blockData: OpenQuestionBlockData;
    updatedAt: BlockProps['updatedAt'];
    isEditorPreview: BlockProps['isEditorPreview'];
    isAnimated: BlockProps['isAnimated'];
    onFinished: BlockProps['onFinished'];
}) => {
    const labelId = useId();
    const textAreaRef = useRef<HTMLTextAreaElement>(null);

    const {
        userDataLocal,
        setUserDataLocal,
    } = useBlockLocalUserData<OpenQuestionBlockLocalData>(
        subjectId,
        id,
        updatedAt,
        isEditorPreview,
        {
            answer: '',
        },
    );

    const [isIncreasedTextSizeEnabled] = useLocalStorage(
        'isIncreasedTextSizeEnabled',
        false,
    );

    const [hasFinished, setHasFinished] = useState(
        userDataLocal.answer.length >= MIN_ANSWER_LENGTH,
    );

    useEffect(() => {
        if (userDataLocal.answer.length >= MIN_ANSWER_LENGTH) {
            setHasFinished(true);
            onFinished?.();
        }
    }, []);

    return (
        <UserMessageBlockViewerContainer
            label={blockData.question}
            labelId={labelId}
            labelClassName="text-emerald-800"
            icon={mdiPen}
            iconSize={0.75}
            isCollapsed={hasFinished}
            isAnimated={isAnimated}
            className={
                isIncreasedTextSizeEnabled
                    ? styles['openquestionblock--increasedtextsize']
                    : undefined
            }
        >
            <div className={styles['openquestionblock__answerinputcontainer']}>
                <p
                    className={
                        styles['openquestionblock__answerinputcontainer__name']
                    }
                >
                    <EmojiText>
                        {hasFinished ? blockData.question : 'Antwoord'}
                    </EmojiText>
                </p>
                <textarea
                    ref={textAreaRef}
                    aria-labelledby={labelId}
                    className={
                        styles['openquestionblock__answerinputcontainer__input']
                    }
                    value={userDataLocal.answer}
                    placeholder="Vul hier je antwoord in"
                    onChange={(e) => {
                        setUserDataLocal({
                            ...userDataLocal,
                            answer: e.target.value,
                        });
                    }}
                />
            </div>
            {!hasFinished && (
                <div className={styles['openquestionblock__buttoncontainer']}>
                    <TextButton
                        onClick={() => {
                            // Move focus back to the input
                            textAreaRef.current?.focus();

                            // Continue chat
                            onFinished?.();
                            setHasFinished(true);
                        }}
                        icon={mdiCheckCircle}
                        className={
                            styles['openquestionblock__buttoncontainer__button']
                        }
                        isEnabled={
                            userDataLocal.answer.length >= MIN_ANSWER_LENGTH
                        }
                        isSmall
                    >
                        Opslaan
                    </TextButton>
                </div>
            )}
        </UserMessageBlockViewerContainer>
    );
};

export default OpenQuestionBlock;
